// =========================
// context/FeatureContext.js — PLATFORM-003 (runtime feature gate)
//
// Provides the runtime feature-visibility map to the whole app so components
// gate on `useFeature('some_key')` instead of a build-time env var. The provider
// fetches /api/get-feature-state once on mount (and exposes refresh()).
//
// Fail-closed by construction: the default context value returns false for every
// feature, and the provider seeds an empty map, so a feature is OFF (dark) until
// the server explicitly says it is on for this user. Components rendered without
// a provider (e.g. in isolation tests) therefore see every feature as off — the
// same safe default as the pre-PLATFORM-003 build-time flag when unset.
// =========================

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { fetchFeatureState } from '../lib/featureState';

const FeatureContext = createContext({
  features: {},
  loading: false,
  isEnabled: () => false,
  refresh: () => {},
});

export function useFeatureContext() {
  return useContext(FeatureContext);
}

// Primary consumer API: boolean for a single feature key. Fail-closed to false.
export function useFeature(key) {
  const ctx = useContext(FeatureContext);
  return ctx.isEnabled(key);
}

export function FeatureProvider({ children }) {
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    const { features: map } = await fetchFeatureState();
    setFeatures(map || {});
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isEnabled = useCallback(
    (key) => features[key] === true,
    [features]
  );

  return (
    <FeatureContext.Provider value={{ features, loading, isEnabled, refresh }}>
      {children}
    </FeatureContext.Provider>
  );
}

export default FeatureContext;
